/**
 * Timezone utility for formatting dates in tenant's or warehouse's local timezone.
 * 
 * CRITICAL: Our database uses "timestamp without time zone" but stores UTC values.
 * API responses may return ISO strings WITHOUT timezone suffix (e.g. "2026-03-18T01:57:46").
 * JavaScript's `new Date("...")` interprets such strings as LOCAL time, not UTC.
 * 
 * ALL timestamp parsing in this module uses `parseUTC()` to guarantee correct interpretation.
 */

/**
 * Parse a timestamp string as UTC. Handles:
 * - ISO strings with "Z" suffix → already UTC
 * - ISO strings with "+/-" offset → already has timezone
 * - ISO strings WITHOUT timezone → treated as UTC (appends "Z")
 * - Date objects → passed through
 * 
 * @param {string|Date} dateString - Timestamp from API
 * @returns {Date} Date object correctly representing the UTC instant
 */
export const parseUTC = (dateString) => {
  if (!dateString) return null;
  if (dateString instanceof Date) return dateString;
  const s = String(dateString);
  // Already has timezone info → parse as-is
  if (s.endsWith('Z') || s.endsWith('z') || /[+-]\d{2}:\d{2}$/.test(s) || /[+-]\d{4}$/.test(s)) {
    return new Date(s);
  }
  // No timezone info → append Z to treat as UTC
  return new Date(s + 'Z');
};

/**
 * Get the tenant timezone from localStorage user data.
 * Falls back to browser-detected timezone, then to America/New_York.
 * 
 * @returns {string} IANA timezone string
 */
export const getTenantTimezone = () => {
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      if (user.tenant_timezone) {
        return user.tenant_timezone;
      }
    }
  } catch (e) {
    console.error('Error reading tenant timezone:', e);
  }
  
  // Fallback to browser timezone, then default
  try {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (browserTz) return browserTz;
  } catch (e) {
    // Intl not supported
  }
  
  return 'America/New_York';
};

/**
 * Get the effective timezone for a warehouse.
 * Uses warehouse timezone if available, otherwise falls back to tenant timezone.
 * 
 * @param {Object} warehouse - Warehouse object with optional timezone property
 * @returns {string} IANA timezone string
 */
export const getWarehouseTimezone = (warehouse) => {
  if (warehouse?.timezone) {
    return warehouse.timezone;
  }
  return getTenantTimezone();
};

/**
 * Get the effective timezone, checking warehouse first, then tenant.
 * 
 * @param {Object} options - Options object
 * @param {Object} options.warehouse - Warehouse object (optional)
 * @param {string} options.warehouseTimezone - Explicit warehouse timezone (optional)
 * @returns {string} IANA timezone string
 */
export const getEffectiveTimezone = ({ warehouse, warehouseTimezone } = {}) => {
  // Explicit warehouse timezone takes priority
  if (warehouseTimezone) {
    return warehouseTimezone;
  }
  // Then check warehouse object
  if (warehouse?.timezone) {
    return warehouse.timezone;
  }
  // Fall back to tenant timezone
  return getTenantTimezone();
};

/**
 * Get timezone abbreviation for a given date and timezone.
 * 
 * @param {Date} date - The date to get abbreviation for
 * @param {string} timeZone - IANA timezone string
 * @returns {string} Timezone abbreviation (e.g., "EST", "PST")
 */
export const getTimezoneAbbr = (date, timeZone) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short'
    }).formatToParts(date);
    
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    return tzPart ? tzPart.value : '';
  } catch (e) {
    return '';
  }
};

/**
 * Format a UTC timestamp for display in the tenant's timezone.
 * 
 * @param {string|Date} dateString - UTC timestamp
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeDate - Include date portion (default: true)
 * @param {boolean} options.includeTime - Include time portion (default: true)
 * @param {boolean} options.showTimezone - Show timezone abbreviation (default: true)
 * @param {string} options.dateStyle - Date style: 'short', 'medium', 'long' (default: 'medium')
 * @param {string} options.timeStyle - Time style: 'short', 'medium' (default: 'short')
 * @param {Object} options.warehouse - Warehouse object to use for timezone (optional)
 * @param {string} options.warehouseTimezone - Explicit warehouse timezone (optional)
 * @returns {string} Formatted date string in effective timezone
 */
export const formatInTenantTimezone = (dateString, options = {}) => {
  if (!dateString) return '';
  
  const {
    includeDate = true,
    includeTime = true,
    showTimezone = true,
    dateStyle = 'medium',
    timeStyle = 'short',
    warehouse,
    warehouseTimezone
  } = options;
  
  const date = parseUTC(dateString);
  if (!date || isNaN(date.getTime())) return '';
  
  // Use warehouse timezone if available, otherwise tenant timezone
  const timeZone = getEffectiveTimezone({ warehouse, warehouseTimezone });
  
  const formatOptions = { timeZone };
  if (includeDate) formatOptions.dateStyle = dateStyle;
  if (includeTime) formatOptions.timeStyle = timeStyle;
  
  try {
    const formatted = new Intl.DateTimeFormat('en-US', formatOptions).format(date);
    
    if (showTimezone && includeTime) {
      const tzAbbr = getTimezoneAbbr(date, timeZone);
      return `${formatted} (${tzAbbr})`;
    }
    
    return formatted;
  } catch (e) {
    // Fallback to basic formatting
    return date.toLocaleString();
  }
};

/**
 * Format a UTC timestamp for display in warehouse's timezone.
 * Convenience wrapper for formatInTenantTimezone with warehouse context.
 * 
 * @param {string|Date} dateString - UTC timestamp
 * @param {Object} warehouse - Warehouse object with optional timezone property
 * @param {Object} options - Additional formatting options (same as formatInTenantTimezone)
 * @returns {string} Formatted date string in warehouse timezone
 */
export const formatInWarehouseTimezone = (dateString, warehouse, options = {}) => {
  return formatInTenantTimezone(dateString, { ...options, warehouse });
};

/**
 * Format a timestamp showing relative date with full timestamp in parentheses.
 * Example: "Today at 7:23 AM (EST)" or "Jan 2, 2026 at 7:23 AM (EST)"
 * 
 * @param {string|Date} dateString - UTC timestamp
 * @param {Object} options - Options object
 * @param {Object} options.warehouse - Warehouse object to use for timezone (optional)
 * @param {string} options.warehouseTimezone - Explicit warehouse timezone (optional)
 * @returns {string} Formatted string with relative date
 */
export const formatRelativeWithTimezone = (dateString, options = {}) => {
  if (!dateString) return '';
  
  const date = parseUTC(dateString);
  if (!date || isNaN(date.getTime())) return '';
  
  const { warehouse, warehouseTimezone } = options;
  const timeZone = getEffectiveTimezone({ warehouse, warehouseTimezone });
  const now = new Date();
  
  // Convert both to effective timezone for comparison
  const dateInTz = new Date(date.toLocaleString('en-US', { timeZone }));
  const nowInTz = new Date(now.toLocaleString('en-US', { timeZone }));
  
  // Check if same day in effective timezone
  const isSameDay = dateInTz.toDateString() === nowInTz.toDateString();
  
  // Check if yesterday
  const yesterday = new Date(nowInTz);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = dateInTz.toDateString() === yesterday.toDateString();
  
  // Format time
  const timeStr = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
  
  const tzAbbr = getTimezoneAbbr(date, timeZone);
  
  if (isSameDay) {
    return `Today at ${timeStr} (${tzAbbr})`;
  } else if (isYesterday) {
    return `Yesterday at ${timeStr} (${tzAbbr})`;
  } else {
    const dateStr = new Intl.DateTimeFormat('en-US', {
      timeZone,
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
    return `${dateStr} at ${timeStr} (${tzAbbr})`;
  }
};

/**
 * Format timestamp for audit displays.
 * Shows: "Jan 2, 2026, 7:23 AM (EST)"
 * 
 * @param {string|Date} dateString - UTC timestamp
 * @returns {string} Formatted audit timestamp
 */
export const formatAuditTimestamp = (dateString) => {
  return formatInTenantTimezone(dateString, {
    includeDate: true,
    includeTime: true,
    showTimezone: true,
    dateStyle: 'medium',
    timeStyle: 'short'
  });
};

/**
 * Format time only for audit item counts.
 * Shows: "7:23 AM (EST)"
 * 
 * @param {string|Date} dateString - UTC timestamp
 * @returns {string} Formatted time with timezone
 */
export const formatAuditTime = (dateString) => {
  if (!dateString) return '';
  
  const date = parseUTC(dateString);
  if (!date || isNaN(date.getTime())) return '';
  
  const timeZone = getTenantTimezone();
  
  const timeStr = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
  
  const tzAbbr = getTimezoneAbbr(date, timeZone);
  return `${timeStr} (${tzAbbr})`;
};

/**
 * Get full timestamp with timezone for tooltips.
 * Shows: "January 2, 2026 at 7:23 AM EST"
 * 
 * @param {string|Date} dateString - UTC timestamp
 * @returns {string} Full timestamp for tooltip
 */
export const getFullTimestampTooltip = (dateString) => {
  if (!dateString) return '';
  
  const date = parseUTC(dateString);
  if (!date || isNaN(date.getTime())) return '';
  
  const timeZone = getTenantTimezone();
  
  const dateStr = new Intl.DateTimeFormat('en-US', {
    timeZone,
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
  
  const timeStr = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
  
  const tzAbbr = getTimezoneAbbr(date, timeZone);
  return `${dateStr} at ${timeStr} ${tzAbbr}`;
};


/**
 * Format a UTC timestamp as relative "time ago" respecting tenant timezone.
 * Returns: "just now", "2m ago", "1h ago", "3h ago", "Yesterday 2:30 PM", "Jan 15 at 2:30 PM"
 *
 * @param {string|Date} dateString - UTC timestamp
 * @returns {string} Relative time string
 */
export const formatTimeAgo = (dateString) => {
  if (!dateString) return '';

  const date = parseUTC(dateString);
  if (!date || isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now - date;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);

  // Future timestamps (clock skew / wrong timezone) — show "just now"
  if (diffMs < 0) return 'just now';

  if (diffSeconds < 60) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;

  // Beyond 24h, fall back to formatted date in tenant timezone
  return formatRelativeWithTimezone(dateString);
};

/**
 * Compact timestamp for table cells — "Today 2:30 PM", "Yesterday 2:30 PM", "Jan 15 2:30 PM"
 * Omits timezone abbreviation for compactness.
 *
 * @param {string|Date} dateString - UTC timestamp
 * @returns {string}
 */
export const formatTimestampCompact = (dateString) => {
  if (!dateString) return '';

  const date = parseUTC(dateString);
  if (!date || isNaN(date.getTime())) return '';

  const timeZone = getTenantTimezone();
  const now = new Date();

  const dateInTz = new Date(date.toLocaleString('en-US', { timeZone }));
  const nowInTz = new Date(now.toLocaleString('en-US', { timeZone }));

  const isSameDay = dateInTz.toDateString() === nowInTz.toDateString();

  const yesterday = new Date(nowInTz);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = dateInTz.toDateString() === yesterday.toDateString();

  const timeStr = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);

  if (isSameDay) return `Today ${timeStr}`;
  if (isYesterday) return `Yesterday ${timeStr}`;

  const dateStr = new Intl.DateTimeFormat('en-US', {
    timeZone,
    month: 'short',
    day: 'numeric',
  }).format(date);
  return `${dateStr} ${timeStr}`;
};
